import React, { useEffect, useState } from "react";
import LoadingScreen from "../components/LoadingScreen";
import {
  Box,
  Typography,
  Paper,
  Button,
  Divider,
  Stack,
  Breadcrumbs,
  Link,
  Card,
  CardMedia,
  CardContent,
} from "@mui/material";
import ReplayIcon from "@mui/icons-material/Replay";
import { useNavigate, useSearchParams } from "react-router-dom";
import HomeIcon from "@mui/icons-material/Home";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import api from "../api/axios";
import ReviewDialog from "../components/reviews/ReviewDialog";
import { colors } from "../utils/theme";
import { hasMedia } from "../utils/productMedia";

const CustomerOrders = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isBuyAgain = searchParams.get("type") === "buy-again";
  const user = JSON.parse(localStorage.getItem("user"));
  const [orders, setOrders] = useState([]);
  const [buyAgainProducts, setBuyAgainProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openReview, setOpenReview] = useState(false);
  const [reviewCtx, setReviewCtx] = useState(null);

  const openReviewDialog = async (productId, orderId) => {
    try {
      const res = await api.get(
        `/reviews/eligibility/${productId}/${orderId}`
      );

      setReviewCtx({
        productId,
        orderId,
        mode: res.data.mode,
        reviewId: res.data.reviewId,
      });

      setOpenReview(true);
    } catch {
      alert("Unable to open review");
    }
  };

  useEffect(() => {
    if (!user?.username) return;

    if (isBuyAgain) {
      api
        .get(`/orders/customer/${user.username}?type=buy-again`)
        .then((res) => setBuyAgainProducts((res.data.buyAgainProducts || []).filter(hasMedia)))
        .catch(() => setBuyAgainProducts([]))
        .finally(() => setLoading(false));
    } else {
      api
        .get(`/orders/customer/${user.username}`)
        .then((res) => {
          setOrders(res.data.orders || []);
        })
        .catch(() => setOrders([]))
        .finally(() => setLoading(false));
    }
  }, [user?.username, isBuyAgain]);

  if (loading) {
    return (
      <Box sx={{ maxWidth: 700, mx: "auto", mt: 6 }}>
        <LoadingScreen message="Loading orders..." />
      </Box>
    );
  }

  if (!isBuyAgain && orders.length === 0) {
    return (
      <Box sx={{ maxWidth: 700, mx: "auto", mt: 6 }}>
        <Typography>No orders found</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 900, mx: "auto", mt: 4, minHeight: "100vh", bgcolor: colors.surfaceBright }}>
      {/* Breadcrumb Navigation */}
      <Box sx={{ mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
        >
          <Link
            underline="hover"
            color="inherit"
            href="/customer/dashboard"
            onClick={(e) => {
              e.preventDefault();
              navigate("/customer/dashboard");
            }}
            sx={{ display: 'flex', alignItems: 'center', color: colors.primary, fontSize: 13 }}
          >
            <HomeIcon sx={{ mr: 0.5, fontSize: 18 }} />
            Customer Dashboard
          </Link>
          <Typography color={colors.onSurfaceVariant} sx={{ display: 'flex', alignItems: 'center', fontSize: 13 }}>
            Total Orders
          </Typography>
        </Breadcrumbs>
        <Button
          size="small"
          variant="text"
          startIcon={<HelpOutlineIcon />}
          onClick={() => navigate("/customer/dashboard")}
          sx={{ textTransform: 'none', color: colors.primary, fontWeight: 600, fontSize: 13 }}
        >
          Help & CS
        </Button>
      </Box>

      <Typography variant="h5" fontWeight={700} mb={3} sx={{ color: colors.onSurface }}>
        {isBuyAgain ? "Buy Again" : "Total Orders"}
      </Typography>

      {isBuyAgain ? (
        /* BUY AGAIN VIEW — matches BrowseHistory card style */
        <Box sx={{ columnCount: { xs: 2, sm: 3, md: 4, lg: 5 }, columnGap: 2 }}>
          {buyAgainProducts.length === 0 ? (
            <Box sx={{ breakInside: "avoid" }}>
              <Card sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
                <Typography color="textSecondary">No buy again items yet</Typography>
              </Card>
            </Box>
          ) : (
            buyAgainProducts.map((item, i) => (
              <Box key={item.productId || i} sx={{ breakInside: "avoid", mb: 2 }}>
                <Card sx={{ borderRadius: 2, position: "relative", transition: "0.2s", "&:hover": { boxShadow: 6 } }}>
                  <Box sx={{ cursor: "pointer" }} onClick={() => navigate(`/product/${item.productId}`)}>
                    <CardMedia
                      component="img"
                      image={item.imageUrl || "/placeholder-product.png"}
                      alt={item.title}
                      sx={{ width: "100%", objectFit: "contain" }}
                    />
                    <CardContent sx={{ pt: 1.5, pb: "12px !important" }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.3, mb: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {item.title || "Product"}
                      </Typography>
                      <Button
                        size="small"
                        variant="contained"
                        fullWidth
                        onClick={(e) => { e.stopPropagation(); navigate(`/delivery-location/${item.productId}`); }}
                        sx={{ bgcolor: "#FFD814", color: "#000", textTransform: "none", fontWeight: 600, fontSize: 12, "&:hover": { bgcolor: "#e6c512" } }}
                      >
                        Buy it again
                      </Button>
                    </CardContent>
                  </Box>
                </Card>
              </Box>
            ))
          )}
        </Box>
      ) : (
        orders.map((order) => (
          <Paper key={order._id} sx={{ mb: 3, borderRadius: 1, overflow: "hidden", boxShadow: "0 2px 4px rgba(0,0,0,0.08)", border: "1px solid", borderColor: colors.outlineVariant }}>
            <Box sx={{ bgcolor: colors.surfaceContainerLow, p: 2 }}>
              <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1}>
                <Typography fontSize={14} sx={{ color: colors.onSurfaceVariant }}><b style={{ color: colors.textMain }}>ORDER PLACED</b><br />{new Date(order.createdAt).toLocaleDateString()}</Typography>
                <Typography fontSize={14} sx={{ color: colors.onSurfaceVariant }}><b style={{ color: colors.textMain }}>TOTAL</b><br />₹{Number(order.total).toLocaleString("en-IN")}</Typography>
                <Typography fontSize={14} sx={{ color: colors.onSurfaceVariant }}><b style={{ color: colors.textMain }}>ORDER #</b><br />{order._id.slice(-8)}</Typography>
              </Stack>
            </Box>
            <Box sx={{ p: 2 }}>
              <Typography fontWeight={600} mb={1} sx={{ color: colors.onSurface, fontSize: 14 }}>Status: <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, px: 1, py: 0.3, borderRadius: 10, bgcolor: "rgba(254,152,0,0.1)", border: "1px solid", borderColor: "rgba(254,152,0,0.2)", color: colors.secondaryContainer, fontWeight: 700, fontSize: 12, ml: 0.5 }}>{order.tracking?.at(-1)?.label || "Processing"}</Box></Typography>
              <Divider sx={{ my: 1, borderColor: colors.outlineVariant }} />
              <Stack direction="row" spacing={2} alignItems="center">
                <Button size="small" variant="outlined" onClick={() => navigate(`/customer/orders/${order._id}`)} sx={{ borderColor: colors.outlineVariant, color: colors.onSurfaceVariant, textTransform: "none", fontWeight: 600, fontSize: 12, "&:hover": { borderColor: colors.primary, color: colors.primary } }}>View order details</Button>
              </Stack>
            </Box>
          </Paper>
        ))
      )}

      {reviewCtx && (
        <ReviewDialog
          open={openReview}
          onClose={() => setOpenReview(false)}
          productId={reviewCtx.productId}
          orderId={reviewCtx.orderId}
          mode={reviewCtx.mode}
          reviewId={reviewCtx.reviewId}
        />
      )}
    </Box>
  );
};

export default CustomerOrders;